/**
 * Workspaces and folders (spec M1-B1 §5.1, §5.2, §7.1).
 *
 * The sidebar's `+ Add` menu, the workspace home and the folder rows all write through these.
 * A folder move that would create a cycle is refused by the **service** (`FOLDER_CYCLE`), not
 * here: the client does not hold the whole folder tree of a 404-board workspace.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { useBoardInvalidation } from './hooks';

export function useWorkspaces() {
  const trpc = useTRPC();
  return useQuery({ ...trpc.workspaces.list.queryOptions({}), staleTime: 30_000 });
}

export function useWorkspace(workspaceId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.workspaces.get.queryOptions({ id: workspaceId ?? '0' }),
    enabled: workspaceId !== null,
    staleTime: 30_000,
  });
}

export function useWorkspaceMembers(workspaceId: string | null) {
  const trpc = useTRPC();
  return useQuery({
    ...trpc.workspaces.members.queryOptions({ id: workspaceId ?? '0' }),
    enabled: workspaceId !== null,
    staleTime: 60_000,
  });
}

export function useRecentBoards(limit = 10) {
  const trpc = useTRPC();
  return useQuery({ ...trpc.shell.recentBoards.queryOptions({ limit }), staleTime: 30_000 });
}

export function useWorkspaceMutations() {
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();

  const create = useMutation({
    mutationFn: (input: { name: string; kind?: 'open' | 'closed'; description?: string }) =>
      client.workspaces.create.mutate(input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (input: { id: string; name?: string; description?: string; kind?: 'open' | 'closed' }) =>
      client.workspaces.update.mutate(input),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => client.workspaces.remove.mutate({ id }),
    onSuccess: invalidate,
  });
  const addMembers = useMutation({
    mutationFn: (input: { id: string; userIds: string[]; role?: 'owner' | 'member' }) =>
      client.workspaces.addMembers.mutate(input),
    onSuccess: invalidate,
  });
  const removeMembers = useMutation({
    mutationFn: (input: { id: string; userIds: string[] }) =>
      client.workspaces.removeMembers.mutate(input),
    onSuccess: invalidate,
  });

  return { create, update, remove, addMembers, removeMembers };
}

export function useFolderMutations() {
  const client = useTRPCClient();
  const invalidate = useBoardInvalidation();

  const create = useMutation({
    mutationFn: (input: { workspaceId: string; name: string; parentFolderId?: string; color?: string }) =>
      client.folders.create.mutate(input),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: (input: {
      id: string;
      name?: string;
      color?: string;
      parentFolderId?: string | null;
      workspaceId?: string;
    }) => client.folders.update.mutate(input),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => client.folders.remove.mutate({ id }),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}
